import { ApiError, apiRequest, apiRequestPaged, type PagedResult } from './client'

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

export type WfhRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'logged'

/** Kind of work-location day (prototype redesign). */
export type WfhRequestType = 'wfh' | 'business'

/** Derived timing chip shown on rows and register records. */
export type WfhTimingFlag = 'same_day' | 'retrospective' | 'logged_late' | null

export type BusinessCategory =
  | 'Client or external meeting'
  | 'Conference or event'
  | 'Training or course'
  | 'Site visit'
  | 'Other'

export type HalfDaySlot = 'AM' | 'PM'

export interface WfhHalfDay {
  date: string
  slot: HalfDaySlot
}

export interface WfhDocRef {
  id: string
  filename: string
  mimeType: string
  byteSize: number
  uploadedAt: string
}

export interface WfhRequest {
  id: string
  employeeId: string
  /** Requester's display name — populated on the approver inbox. */
  employeeName: string | null
  requestType: WfhRequestType
  startDate: string
  endDate: string
  /** Per-date half days; a date absent from this list is a full WFH day. */
  halfDays: WfhHalfDay[]
  /** Optional attachments. */
  documents: WfhDocRef[]
  status: WfhRequestStatus
  reason: string
  /** Out-on-business only. */
  category: string | null
  location: string | null
  purpose: string | null
  /** Derived server-side: same_day / retrospective / logged_late, or null. */
  timingFlag: WfhTimingFlag
  rejectReason: string | null
  approverNote: string | null
  primaryApproverId: string | null
  decidedById: string | null
  decidedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateWfhRequestInput {
  requestType?: WfhRequestType
  startDate: string
  endDate: string
  reason: string
  /** Out-on-business only (required when requestType=business). */
  category?: string
  location?: string
  purpose?: string
  /** Per-date half days; omit a date to keep it a full WFH day. */
  halfDays?: WfhHalfDay[]
}

export interface WfhDecisionInput {
  decision: 'approve' | 'reject'
  approverNote?: string
  rejectReason?: string
}

export type WfhDaySlot_ = 'FULL' | 'AM' | 'PM'

interface ListParams {
  page?: number
  limit?: number
}

function toQuery(params: ListParams): string {
  const qs = new URLSearchParams()
  if (params.page != null) qs.set('page', String(params.page))
  if (params.limit != null) qs.set('limit', String(params.limit))
  const s = qs.toString()
  return s ? `?${s}` : ''
}

/** File a WFH request for the current user. Lands PENDING with the manager. */
export function createWfhRequest(input: CreateWfhRequestInput): Promise<WfhRequest> {
  return apiRequest<WfhRequest>('/api/v1/wfh', { method: 'POST', body: input })
}

/** The current user's own WFH requests, newest first (cursor-paginated). */
export function listMyWfhRequests(params: ListParams = {}): Promise<PagedResult<WfhRequest>> {
  return apiRequestPaged<WfhRequest>(`/api/v1/wfh/mine${toQuery(params)}`)
}

/** The approver inbox: PENDING requests routed to the current user. */
export function listPendingWfhRequests(
  params: ListParams = {},
): Promise<PagedResult<WfhPendingApproval>> {
  return apiRequestPaged<WfhPendingApproval>(`/api/v1/wfh/pending${toQuery(params)}`)
}

/** The current user's decision history: requests they approved or rejected. */
export function listReviewedWfhRequests(
  params: ListParams = {},
): Promise<PagedResult<WfhRequest>> {
  return apiRequestPaged<WfhRequest>(`/api/v1/wfh/reviewed${toQuery(params)}`)
}

/** Approve or reject a pending WFH request. */
export function decideWfhRequest(
  id: string,
  input: WfhDecisionInput,
): Promise<WfhRequest> {
  return apiRequest<WfhRequest>(`/api/v1/wfh/${id}/decide`, {
    method: 'POST',
    body: input,
  })
}

/** Cancel one of the current user's own WFH requests. */
export function cancelWfhRequest(id: string): Promise<WfhRequest> {
  return apiRequest<WfhRequest>(`/api/v1/wfh/${id}/cancel`, { method: 'POST' })
}

/** Attach an optional document to a WFH request (multipart). Owner-only. */
export async function uploadWfhDocument(
  requestId: string,
  file: File,
): Promise<WfhDocRef> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(
    `${API_BASE}/api/v1/wfh/${encodeURIComponent(requestId)}/documents`,
    { method: 'POST', credentials: 'include', body: form },
  )
  const payload = (await res.json().catch(() => null)) as
    | { data?: WfhDocRef; error?: string }
    | null
  if (!res.ok) {
    throw new ApiError(payload?.error ?? 'Upload failed', res.status, payload)
  }
  return (payload as { data: WfhDocRef }).data
}

/** URL that streams a document's bytes through the api pod (cookie-authed). */
export function getWfhDocumentUrl(requestId: string, documentId: string): string {
  return `${API_BASE}/api/v1/wfh/${encodeURIComponent(requestId)}/documents/${encodeURIComponent(
    documentId,
  )}`
}

// ── Approvals context (prototype redesign) ──────────────────────────────────

export interface WfhUsageCounters {
  wfhDaysThisMonth: number
  wfhDaysThisQuarter: number
}

export interface WfhContextNote {
  text: string
}

export interface WfhPendingApproval {
  request: WfhRequest
  usage: WfhUsageCounters
  notes: WfhContextNote[]
  overdue: boolean
}

export interface WfhTeamTodayGroup {
  key: 'office' | 'wfh' | 'business' | 'leave'
  label: string
  count: number
  people: string[]
}

export interface WfhApprovalsSummary {
  date: string
  groups: WfhTeamTodayGroup[]
  overdueCount: number
  /** Team's out-on-business records — notify-only, no buttons. */
  businessFyi: WfhRequest[]
}

/** Approvals-tab header: today strip + overdue count. Manager+ only. */
export function getWfhApprovalsSummary(): Promise<WfhApprovalsSummary> {
  return apiRequest<WfhApprovalsSummary>('/api/v1/wfh/approvals/summary')
}

// ── HR attendance register ──────────────────────────────────────────────────

export interface WfhRegisterRow {
  id: string
  employeeId: string
  employeeName: string
  teamName: string | null
  requestType: WfhRequestType
  status: WfhRequestStatus
  /** Derived: pending whose end date has passed with no decision. */
  unreviewed: boolean
  startDate: string
  endDate: string
  days: number
  timingFlag: WfhTimingFlag
  detail: string
}

export interface WfhRegisterStats {
  wfhDaysApproved: number
  businessDaysLogged: number
  flaggedWfh: string
  unreviewedCount: number
}

export interface WfhRegister {
  rows: WfhRegisterRow[]
  stats: WfhRegisterStats
  outstanding: WfhRegisterRow[]
  total: number
}

export interface WfhRegisterParams {
  from?: string
  to?: string
  type?: WfhRequestType
  status?: string
  q?: string
}

/** HR attendance register (HR+ only): rows, stats, outstanding list. */
export function getWfhRegister(params: WfhRegisterParams = {}): Promise<WfhRegister> {
  const qs = new URLSearchParams()
  if (params.from) qs.set('from', params.from)
  if (params.to) qs.set('to', params.to)
  if (params.type) qs.set('type', params.type)
  if (params.status) qs.set('status', params.status)
  if (params.q?.trim()) qs.set('q', params.q.trim())
  const s = qs.toString()
  return apiRequest<WfhRegister>(`/api/v1/wfh/register${s ? `?${s}` : ''}`)
}
